import _classPrivateFieldInitSpec from "@babel/runtime/helpers/classPrivateFieldInitSpec";
import _classPrivateFieldGet from "@babel/runtime/helpers/classPrivateFieldGet2";
import _classPrivateFieldSet from "@babel/runtime/helpers/classPrivateFieldSet2";
import { uniq } from "lodash";
import { isStrictMode, warnAboutStrictMode } from "./isStrictMode.js";
import { getV2RuntimeFromDll } from "./getV2RuntimeFromDll.js";

// Note: `prefix` is a native prop on Element, but it's only used in XML documents.
const allowedNativeProps = new Set(["prefix"]);
var _registry = /*#__PURE__*/new WeakMap();
var _isolated = /*#__PURE__*/new WeakMap();
export class CustomTemplateRegistry {
  constructor(isolated) {
    _classPrivateFieldInitSpec(this, _registry, new Map());
    _classPrivateFieldInitSpec(this, _isolated, void 0);
    _classPrivateFieldSet(_isolated, this, isolated);
  }
  define(tagName, constructor) {
    var _constructor$proxy$pr, _constructor$proxy, _compatibleConstructo, _compatibleConstructo2;
    let registered = _classPrivateFieldGet(_registry, this).has(tagName);
    if (registered) {
      // When open launchpad, the storyboard will be updated.
      // However, we can't *undefine* a custom element.
      // Just ignore re-registering custom templates.
      // eslint-disable-next-line no-console
      console.warn(`Custom template of "${tagName}" already registered.`);
    } else {
      registered = !!customElements.get(tagName);
      if (registered) {
        // eslint-disable-next-line no-console
        console.warn(`Custom template of "${tagName}" already defined by customElements.`);
      }
    }

    // Transform legacy `proxy.properties[].asVariable` as states.
    const strict = isStrictMode();
    const proxyProperties = (_constructor$proxy$pr = (_constructor$proxy = constructor.proxy) === null || _constructor$proxy === void 0 ? void 0 : _constructor$proxy.properties) !== null && _constructor$proxy$pr !== void 0 ? _constructor$proxy$pr : {};
    const validProxyProps = [];
    const legacyTplVariables = [];
    for (const [key, value] of Object.entries(proxyProperties)) {
      if (value.asVariable) {
        warnAboutStrictMode(strict, "Template `asVariable`", tagName, key);
        // istanbul ignore next
        if (!strict) {
          // For existed TPL usage, treat it as a STATE.
          legacyTplVariables.push(key);
        }
      } else if (value.mergeProperty || value.refTransform) {
        // eslint-disable-next-line no-console
        console.error("Template `mergeProperty` and `refTransform` are dropped in v3:", tagName, key);
      } else if (value.ref) {
        validProxyProps.push([key, value]);
      }
      // Else: documentation only, for exposed states.
    }
    const compatibleConstructor = {
      ...constructor,
      proxy: {
        ...constructor.proxy,
        properties: Object.fromEntries(validProxyProps)
      },
      state: (constructor.state ? strict ? constructor.state : constructor.state.map(item => ({
        // Make `expose` defaults to true in non-strict mode.
        expose: true,
        ...item
      })) : []).concat(legacyTplVariables.map(tpl => ({
        name: tpl,
        expose: true
      })))
    };

    // Now we allow re-register custom template
    _classPrivateFieldGet(_registry, this).set(tagName, {
      ...compatibleConstructor,
      name: tagName
    });
    const exposedStates = getExposedStates(compatibleConstructor.state);
    const proxyMethods = Object.entries((_compatibleConstructo = (_compatibleConstructo2 = compatibleConstructor.proxy) === null || _compatibleConstructo2 === void 0 ? void 0 : _compatibleConstructo2.methods) !== null && _compatibleConstructo !== void 0 ? _compatibleConstructo : {});
    const props = exposedStates.concat(validProxyProps.map(entry => entry[0]));
    const methods = proxyMethods.map(entry => entry[0]);
    const nativeProps = props.concat(methods).filter(prop => prop in HTMLElement.prototype && !allowedNativeProps.has(prop));
    if (nativeProps.length > 0) {
      warnAboutStrictMode(strict, "Using native HTMLElement properties as template properties or methods", tagName, ...nativeProps);
      // istanbul ignore next
      if (strict) {
        throw new Error(`In custom template "${tagName}", ${nativeProps.map(p => `"${p}"`).join(", ")} are native HTMLElement properties, and should be avoid to be used as brick properties or methods.`);
      }
    }
    if (registered) {
      return;
    }
    class TplElement extends HTMLElement {
      get $$typeof() {
        return "custom-template";
      }
      static get _dev_only_definedProperties() {
        return props;
      }
      static get _dev_only_definedMethods() {
        return methods;
      }
      $$getElementByRef(ref) {
        var _this$$$tplStateStore;
        return (_this$$$tplStateStore = this.$$tplStateStore) === null || _this$$$tplStateStore === void 0 || (_this$$$tplStateStore = _this$$$tplStateStore.hostBrick) === null || _this$$$tplStateStore === void 0 || (_this$$$tplStateStore = _this$$$tplStateStore.tplHostMetadata) === null || _this$$$tplStateStore === void 0 || (_this$$$tplStateStore = _this$$$tplStateStore.internalBricksByRef.get(ref)) === null || _this$$$tplStateStore === void 0 ? void 0 : _this$$$tplStateStore.element;
      }
      connectedCallback() {
        let shadowRoot = this.shadowRoot;
        if (!shadowRoot) {
          shadowRoot = this.attachShadow({
            mode: "open"
          });
        }
        const fragment = document.createDocumentFragment();
        const style = document.createElement("style");
        style.textContent = ":host{display:block}:host([hidden]){display:none}";
        const slot = document.createElement("slot");
        fragment.appendChild(style);
        fragment.appendChild(slot);
        shadowRoot.appendChild(fragment);
      }
      disconnectedCallback() {
        if (this.shadowRoot) {
          this.shadowRoot.textContent = "";
        }
      }
    }
    for (const propName of exposedStates) {
      if (validProxyProps.some(entry => entry[0] === propName)) {
        // eslint-disable-next-line no-console
        console.error(`Cannot define an exposed state that is also a proxy property: "${propName}" in ${tagName}`);
        continue;
      }
      Object.defineProperty(TplElement.prototype, propName, {
        get() {
          return this.$$tplStateStore.getValue(propName);
        },
        set(value) {
          var _this$$$tplStateStore2;
          // 在 mount 过程中，先设置属性，再设置 `$$tplStateStore`，这样，当触发属性设置时，
          // 避免初始化的一次 state update 操作及其 onChange 事件。
          (_this$$$tplStateStore2 = this.$$tplStateStore) === null || _this$$$tplStateStore2 === void 0 || _this$$$tplStateStore2.updateValue(propName, value, "replace");
        },
        enumerable: true
      });
    }
    for (const [from, to] of validProxyProps) {
      Object.defineProperty(TplElement.prototype, from, {
        get() {
          var _to$refProperty;
          const element = this.$$getElementByRef(to.ref);
          return element[(_to$refProperty = to.refProperty) !== null && _to$refProperty !== void 0 ? _to$refProperty : from];
        },
        set(value) {
          var _this$$$getElementByR;
          // 同上 exposedState.set
          const element = (_this$$$getElementByR = this.$$getElementByRef) === null || _this$$$getElementByR === void 0 ? void 0 : _this$$$getElementByR.call(this, to.ref);
          if (element) {
            var _to$refProperty2;
            element[(_to$refProperty2 = to.refProperty) !== null && _to$refProperty2 !== void 0 ? _to$refProperty2 : from] = value;
          }
        },
        enumerable: true
      });
    }
    for (const [from, to] of proxyMethods) {
      Object.defineProperty(TplElement.prototype, from, {
        value() {
          var _to$refMethod;
          const element = this.$$getElementByRef(to.ref);
          return element[(_to$refMethod = to.refMethod) !== null && _to$refMethod !== void 0 ? _to$refMethod : from](...arguments);
        },
        enumerable: true
      });
    }
    customElements.define(tagName, TplElement);
  }
  get(tagName) {
    return _classPrivateFieldGet(_registry, this).get(tagName);
  }
  clearIsolatedRegistry() {
    if (_classPrivateFieldGet(_isolated, this)) {
      _classPrivateFieldGet(_registry, this).clear();
    }
  }
}
function getExposedStates(state) {
  var _state$filter$map;
  // Allow duplicated state names which maybe mutually exclusive.
  return uniq((_state$filter$map = state === null || state === void 0 ? void 0 : state.filter(item => item.expose).map(item => item.name)) !== null && _state$filter$map !== void 0 ? _state$filter$map : []);
}

// istanbul ignore next
function getCustomTemplatesV2() {
  const v2Kit = getV2RuntimeFromDll();
  if (v2Kit) {
    return Object.freeze({
      define(tagName, constructor) {
        return v2Kit.getRuntime().registerCustomTemplate(tagName, constructor);
      }
    });
  }
}

// istanbul ignore next
export const customTemplates = getCustomTemplatesV2() || new CustomTemplateRegistry();
//# sourceMappingURL=CustomTemplates.js.map